// Accessori (2026-10): rosoni e altri accessori a listino, nello stile di Strip LED e Alimentatori.
// Una scheda per famiglia (campo "family" in Accessori.json) con la scelta di finitura e serracavo;
// ogni variante è un codice a sé con il suo prezzo e la sua foto.
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import '../css/CatalogoPage.css';
import '../css/ShopPage.css';
import successSound from '../assets/success.mp3';
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import {
  formatEuro, PdfButton, QtyStepper, AddButton, AddedToast, ChipGroup, MobileFiltersButton, FiltersSheet, SpecGrid, ProductPhoto,
} from './shopUi';

const uniq = (list) => [...new Set(list.filter((v) => v !== undefined && v !== null && v !== ''))];
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

// Colore del pallino finitura
const finishColor = (value) => {
  const s = String(value || '').toLowerCase();
  if (s.includes('nero')) return '#15181C';
  if (s.includes('oro') || s.includes('ottone')) return '#C9A14A';
  if (s.includes('rame')) return '#B87333';
  if (s.includes('bronzo')) return '#8C6A43';
  if (s.includes('titanio') || s.includes('grigio')) return '#9AA1A8';
  if (s.includes('cromo') || s.includes('acciaio') || s.includes('argento')) return 'linear-gradient(135deg,#d9dde1,#9aa3ab)';
  return '#FFFFFF';
};

// Disegni delle famiglie di accessori
const typeIcon = (d) => (
  <svg viewBox="0 0 46 30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
const CATEGORIES = [
  { value: '', key: 'catalog.all', icon: typeIcon(<><rect x="6" y="5" width="14" height="9" rx="1.5" /><rect x="26" y="5" width="14" height="9" rx="1.5" /><rect x="6" y="17" width="14" height="9" rx="1.5" /><rect x="26" y="17" width="14" height="9" rx="1.5" /></>) },
  { value: 'rosoni', key: 'accessories.catRosoni', icon: typeIcon(<><path d="M4 3h38" strokeWidth="2.4" /><path d="M13 4h20v6a3 3 0 0 1-3 3H16a3 3 0 0 1-3-3z" /><path d="M23 13v14" /></>) },
  { value: 'sospensioni', key: 'accessories.catSuspensions', icon: typeIcon(<><path d="M4 3h38" strokeWidth="2.4" /><path d="M15 3v18M31 3v18" /><path d="M10 21h26v4H10z" /></>) },
  { value: 'portalampade', key: 'accessories.catLampholders', icon: typeIcon(<><path d="M23 2v8" /><path d="M17 10h12v8a6 6 0 0 1-12 0z" /><path d="M17 14h12" opacity=".6" /></>) },
  { value: 'fissaggi', key: 'accessories.catFixings', icon: typeIcon(<><path d="M6 5v20" strokeWidth="2.4" /><path d="M7 10h18l6 5-6 5H7" /><circle cx="22" cy="15" r="1.6" /></>) },
];

const matchesAcc = (a, f) =>
  (!f.category || a.category === f.category) &&
  (!f.finitura || a.finitura === f.finitura) &&
  (!f.fori || String(a.fori) === f.fori);

const AccessoryFamilyCard = ({ family, t, user, showPrices, handleDownload, handleAddToCart }) => {
  const [id, setId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const items = family.items;
  const current = items.find((a) => a.id === id) || items[0];
  const finishes = uniq(items.map((a) => a.finitura));
  const clamps = uniq(items.filter((a) => a.finitura === current.finitura).map((a) => a.serracavo));
  const netPrice = user ? current.price * (1 - (user.discount || 0) / 100) : current.price;
  const categoryKey = CATEGORIES.find((c) => c.value === current.category)?.key;

  const pickFinish = (finitura) => {
    const next = items.find((a) => a.finitura === finitura && a.serracavo === current.serracavo) ||
      items.find((a) => a.finitura === finitura);
    if (next) setId(next.id);
  };
  const pickClamp = (serracavo) => {
    const next = items.find((a) => a.finitura === current.finitura && a.serracavo === serracavo);
    if (next) setId(next.id);
  };

  return (
    <article className="sp-card">
      <div className="sp-photo is-contain">
        <ProductPhoto key={current.image} src={current.image ? urlAsset(current.image) : ''} alt={current.name} />
        {categoryKey && (
          <span className="sp-tags"><span className="sp-tag">{t(categoryKey)}</span></span>
        )}
        {current.datasheet && <PdfButton onClick={() => handleDownload(current.datasheet)} label={t('shop.datasheet')} />}
      </div>
      <div className="sp-body">
        <div className="sp-head">
          <strong>{String(current.name || '').trim()}</strong>
          {showPrices && (
            <span className="sp-net">
              {formatEuro(netPrice)}
              {user && <small>{t('shop.net')}</small>}
            </span>
          )}
        </div>
        <SpecGrid
          cols={3}
          cells={[
            { label: t('shop.specSize'), value: current.size },
            current.fori
              ? { label: t('accessories.holes'), value: String(current.fori) }
              : { label: t('accessories.pack'), value: current.confezione },
            { label: t('accessories.material'), value: current.materiale },
          ]}
        />
        {finishes.length > 0 && (
          <div className="sp-variants" role="group" aria-label={t('accessories.finish')}>
            {finishes.map((f) => (
              <button key={f} type="button" className="sp-vchip" aria-pressed={f === current.finitura} onClick={() => pickFinish(f)}>
                <i style={{ background: finishColor(f) }} />{cap(f)}
              </button>
            ))}
          </div>
        )}
        {clamps.length > 1 && (
          <div className="sp-variants" role="group" aria-label={t('accessories.cableClamp')}>
            {clamps.map((c) => (
              <button key={c} type="button" className="sp-vchip" aria-pressed={c === current.serracavo} onClick={() => pickClamp(c)}>
                {t('accessories.cableClamp')} {c}
              </button>
            ))}
          </div>
        )}
        <div className="sp-buy">
          <span className="sp-code">
            <span>{t('shop.code')} <b>{current.id}</b></span>
            {showPrices && (
              <span>{t('shop.list')} {user ? <s>{formatEuro(current.price)}</s> : formatEuro(current.price)}</span>
            )}
          </span>
          <div className="sp-actions">
            <QtyStepper value={quantity} onChange={setQuantity} label={t('shop.quantity')} />
            <AddButton label={t('shop.add')} onClick={() => handleAddToCart(current, Math.max(1, parseInt(quantity, 10) || 1))} />
          </div>
        </div>
      </div>
    </article>
  );
};

const Accessori = ({ searchResults, showPrices }) => {
  const { addToCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [accessori, setAccessori] = useState([]);
  const [activeFilters, setActiveFilters] = useState({ category: '', finitura: '', fori: '' });
  const [message, setMessage] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const messageTimer = useRef(null);

  useEffect(() => {
    fetch('/api/products/config/accessori')
      .then((response) => response.json())
      .then((data) => setAccessori((Array.isArray(data) ? data : []).filter((a) => a.active)))
      .catch((error) => console.error('Error loading configuration file:', error));
  }, []);

  useEffect(() => () => clearTimeout(messageTimer.current), []);
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // Base: risultati della ricerca (solo attivi) se ci sono, altrimenti tutti gli attivi
  const baseAcc = searchResults && searchResults.length > 0
    ? searchResults.filter((a) => a.active)
    : accessori;

  // Famiglie: la scheda resta visibile se almeno una variante passa i filtri, e mostra solo quelle
  const families = useMemo(() => {
    const map = new Map();
    baseAcc.forEach((a) => {
      const key = a.family || a.id;
      if (!map.has(key)) map.set(key, { key, items: [] });
      map.get(key).items.push(a);
    });
    return [...map.values()];
  }, [baseAcc]);
  const visibleFamilies = families
    .map((fam) => ({ ...fam, items: fam.items.filter((a) => matchesAcc(a, activeFilters)) }))
    .filter((fam) => fam.items.length > 0);
  const visibleCodes = visibleFamilies.reduce((n, fam) => n + fam.items.length, 0);

  const handleFilterChange = (filterType, filterValue) => {
    setActiveFilters((f) => ({ ...f, [filterType]: f[filterType] === filterValue ? '' : filterValue }));
  };
  const resetFilters = () => setActiveFilters({ category: '', finitura: '', fori: '' });

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play();
  };

  const showMessage = (text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(''), 2400);
  };

  const handleAddToCart = (acc, quantity) => {
    const description = [
      [null, acc.name],
      [t('accessories.finish'), cap(acc.finitura)],
      [t('accessories.cableClamp'), cap(acc.serracavo)],
      [t('accessories.holes'), acc.fori],
      [t('accessories.pack'), acc.confezione],
      [t('shop.specSize'), acc.size],
    ]
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
      .map(([label, value]) => (label ? `${label}: ${value}` : String(value)))
      .join(',\n');

    addToCart({
      code: acc.id,
      description,
      unitPrice: acc.price,
      quantity,
      productType: 'accessorio',
      image: acc.image || null,
      datasheet: acc.datasheet || null,
    });
    playSound();
    showMessage(`${t('accessories.addedToCart')} · ${String(acc.name || '').trim()} × ${quantity}`);
  };

  const handleDownload = (datasheet) => {
    if (!datasheet) return;
    window.open(urlAsset(datasheet), '_blank', 'noopener,noreferrer');
  };

  const finishOptions = uniq(accessori.map((a) => a.finitura));
  const holeOptions = uniq(accessori.map((a) => (a.fori ? String(a.fori) : ''))).sort((a, b) => a - b);
  const presentCategories = new Set(accessori.map((a) => a.category));
  const tiles = CATEGORIES.filter((c) => !c.value || presentCategories.has(c.value));

  const hasFilters = Object.values(activeFilters).some(Boolean);
  const chipGroups = (
    <>
      {finishOptions.length > 0 && (
        <ChipGroup
          title={t('accessories.finish')}
          options={finishOptions.map((f) => ({ value: f, label: cap(f) }))}
          active={activeFilters.finitura}
          onToggle={(v) => handleFilterChange('finitura', v)}
        />
      )}
      {holeOptions.length > 0 && (
        <ChipGroup
          title={t('accessories.holes')}
          options={holeOptions.map((h) => ({ value: h, label: h }))}
          active={activeFilters.fori}
          onToggle={(v) => handleFilterChange('fori', v)}
        />
      )}
    </>
  );
  const sheetCount = ['finitura', 'fori'].filter((k) => activeFilters[k]).length;

  return (
    <div className="cat sp">
      <section className="cat-band">
        <div className="cat-band-in">
          <div className="cat-head">
            <div>
              <h1>{t('sidebar.accessories')}</h1>
              <p>{t('shop.accSubtitle')}</p>
            </div>
            <span className="cat-count">{t('shop.familiesCount', { f: visibleFamilies.length, n: visibleCodes })}</span>
          </div>

          <div className={`cat-mounts ${tiles.length <= 4 ? 'is-4' : 'is-5'}`} role="group" aria-label={t('accessories.family')}>
            {tiles.map((c) => {
              const count = families.filter((fam) => fam.items.some((a) => matchesAcc(a, { ...activeFilters, category: c.value }))).length;
              return (
                <button
                  key={c.value || 'all'}
                  type="button"
                  className="cat-mt"
                  aria-pressed={activeFilters.category === c.value}
                  onClick={() => (c.value ? handleFilterChange('category', c.value) : setActiveFilters((f) => ({ ...f, category: '' })))}
                >
                  {c.icon}
                  <span>{t(c.key)}</span>
                  <small>{count}</small>
                </button>
              );
            })}
          </div>

          <div className="cat-fbar">
            {chipGroups}
            <button type="button" className="cat-reset" onClick={resetFilters} disabled={!hasFilters}>
              {t('catalog.reset')}
            </button>
          </div>
          <MobileFiltersButton
            label={t('catalog.filters')}
            count={sheetCount}
            onOpen={() => setSheetOpen(true)}
            onReset={resetFilters}
            resetLabel={t('catalog.reset')}
            hasFilters={hasFilters}
          />
        </div>
      </section>

      <section className="cat-grid-wrap">
        {visibleFamilies.length > 0 ? (
          <div className="sp-grid">
            {visibleFamilies.map((fam) => (
              <AccessoryFamilyCard
                key={fam.key}
                family={fam}
                t={t}
                user={user}
                showPrices={showPrices}
                handleDownload={handleDownload}
                handleAddToCart={handleAddToCart}
              />
            ))}
          </div>
        ) : (
          <div className="cat-empty">
            <p>{t('shop.noResults')}</p>
            {hasFilters && <button type="button" className="cat-chip" onClick={resetFilters}>{t('catalog.reset')}</button>}
          </div>
        )}
      </section>

      <FiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('catalog.filters')}
        resetLabel={t('catalog.reset')}
        onReset={resetFilters}
        hasFilters={hasFilters}
        showLabel={t('shop.showResults')}
      >
        {chipGroups}
      </FiltersSheet>

      <AddedToast message={message} onGoToCart={() => navigate('/cart')} goToCartLabel={t('shop.goToCart')} />
    </div>
  );
};

export default Accessori;
